import React, { useState, useEffect } from "react"
import { Modal, Button, Form, Alert, Spinner, Badge } from "react-bootstrap"
import { motion, AnimatePresence, Variants } from "framer-motion"
import { FaEdit, FaInfoCircle } from "react-icons/fa"
import axios from "axios"
import Swal from "sweetalert2"
import "../../../css/editPromoCodeModal.css"

interface PromoCode {
  id: number
  code: string
  description: string
  discountPercentage: number
  isActive: boolean
  usedBy: string[]
  createdAt: string
}

interface EditPromoCodeModalProps {
  show: boolean
  onClose: () => void
  promoCode: PromoCode | null
  onUpdated: () => void
}

const EditPromoCodeModal: React.FC<EditPromoCodeModalProps> = ({ 
  show, 
  onClose, 
  promoCode, 
  onUpdated 
}) => {
  const [discountPercentage, setDiscountPercentage] = useState<number>(0)
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  // Animations variants
  const modalVariants: Variants = {
    hidden: { 
      opacity: 0,
      scale: 0.8,
      y: -50
    },
    visible: { 
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 25
      }
    },
    exit: { 
      opacity: 0,
      scale: 0.8,
      y: 50,
      transition: {
        duration: 0.2
      }
    }
  }

  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
    exit: { opacity: 0 }
  }

  useEffect(() => {
    if (promoCode) {
      setDiscountPercentage(promoCode.discountPercentage)
    }
    setError(null)
  }, [promoCode])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (discountPercentage < 0 || discountPercentage > 100) {
      setError("Le pourcentage de remise doit être entre 0 et 100")
      return
    }

    if (!promoCode) {
      setError("Aucun code promo sélectionné")
      return
    }

    if (discountPercentage === promoCode.discountPercentage) {
      setError("Le nouveau pourcentage doit être différent de l'ancien")
      return
    }

    // Confirmation avec SweetAlert2
    const result = await Swal.fire({
      title: 'Confirmer la modification',
      html: `
        <div style="text-align: left;">
          <p><strong>Code:</strong> ${promoCode.code}</p>
          <p><strong>Ancien pourcentage:</strong> ${promoCode.discountPercentage}%</p>
          <p><strong>Nouveau pourcentage:</strong> ${discountPercentage}%</p>
          <div style="background: #fff3cd; padding: 10px; border-radius: 5px; margin-top: 15px; border-left: 4px solid #eb8635;">
            <small><strong>Important:</strong> Cette modification ne s'applique qu'aux nouvelles utilisations du code promo.</small>
          </div>
        </div>
      `,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#2A919D',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Modifier',
      cancelButtonText: 'Annuler',
      customClass: {
        popup: 'swal-custom-popup',
        title: 'swal-custom-title'
      }
    })

    if (!result.isConfirmed) return

    setLoading(true)

    try {
      const token = localStorage.getItem("token")
      if (!token) throw new Error("Token d'authentification manquant")

      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/promo-code/${promoCode.id}`,
        { discountPercentage },
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 10000,
        }
      )

      // Fermer le modal
      onClose()

      // Rafraîchir la liste
      onUpdated()

      // Message de succès
      await Swal.fire({
        title: 'Modifié !',
        html: `
          <div style="text-align: center;">
            <p>Le code promo <strong>"${promoCode.code}"</strong> a été modifié avec succès</p>
            <div style="display: flex; justify-content: center; gap: 10px; margin-top: 15px; flex-wrap: wrap;">
              <span style="background: #dc3545; color: white; padding: 4px 8px; border-radius: 12px; font-size: 12px;">
                Ancien: ${response.data.promoCode.oldPercentage}%
              </span>
              <span style="background: #2A919D; color: white; padding: 4px 8px; border-radius: 12px; font-size: 12px;">
                Nouveau: ${discountPercentage}%
              </span>
            </div>
          </div>
        `,
        icon: 'success',
        timer: 4000,
        timerProgressBar: true,
        showConfirmButton: false,
        toast: true,
        position: 'top-end'
      })

    } catch (err: any) {
      console.error(err)
      let errorMessage = "Impossible de modifier le code promo"
      
      if (err.response?.status === 404) {
        errorMessage = "Ce code promo n'existe plus"
      } else if (err.response?.status === 401) {
        errorMessage = "Session expirée. Veuillez vous reconnecter."
      } else if (err.response?.status === 403) {
        errorMessage = "Vous n'avez pas l'autorisation de modifier ce code promo."
      } else if (err.code === "ECONNABORTED") {
        errorMessage = "Timeout de connexion. Veuillez réessayer."
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message
      }

      Swal.fire({
        title: 'Erreur',
        text: errorMessage,
        icon: 'error',
        confirmButtonColor: '#dc3545'
      })
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    if (!loading) {
      setError(null)
      onClose()
    }
  }

  const getDiscountBadgeVariant = (percentage: number) => {
    if (percentage === 100) return "success"
    if (percentage >= 50) return "warning"
    return "danger"
  }

  
  return (
    <AnimatePresence>
      {show && (
        <Modal
          show={show}
          onHide={handleClose}
          centered
          backdrop="static"
          keyboard={!loading}
          className="edit-promo-modal"
          size="lg"
        >
          <motion.div
            variants={backdropVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <motion.div
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              <Modal.Header closeButton className="border-0 pb-0">
                <Modal.Title className="d-flex align-items-center flex-wrap">
                  <span className="me-2">Modifier le code promo:</span>
                  <span className="text-primary d-block d-sm-inline">{promoCode?.code}</span>
                </Modal.Title>
              </Modal.Header>

              <Modal.Body className="px-3 px-md-4">
                <AnimatePresence>
                  {error && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <Alert variant="danger" className="mb-3">
                        {error}
                      </Alert>
                    </motion.div>
                  )}
                </AnimatePresence>

                {promoCode && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                  >
                    {/* Informations actuelles avec design moderne */}
                    <div className="mb-4 p-3 bg-light rounded-3 border-start border-primary border-4">
                      <div className="d-flex align-items-center mb-3">
                        <FaInfoCircle className="text-primary me-2" />
                        <h6 className="mb-0 fw-bold text-primary">Informations actuelles</h6>
                      </div>
                      <div className="row g-2">
                        <div className="col-12 col-sm-6">
                          <small className="text-muted">Code</small>
                          <div className="fw-semibold text-break">{promoCode.code}</div>
                        </div>
                        <div className="col-12 col-sm-6">
                          <small className="text-muted">Description</small>
                          <div className="fw-semibold text-break">{promoCode.description || "Aucune"}</div>
                        </div>
                        <div className="col-12 col-sm-6">
                          <small className="text-muted">Statut</small>
                          <div>
                            <Badge bg={promoCode.isActive ? "success" : "secondary"}>
                              {promoCode.isActive ? "Actif" : "Inactif"}
                            </Badge>
                          </div>
                        </div>
                        <div className="col-12 col-sm-6">
                          <small className="text-muted">Pourcentage actuel</small>
                          <div>
                            <Badge bg={getDiscountBadgeVariant(promoCode.discountPercentage)} className="px-2 py-1">
                              {promoCode.discountPercentage}%
                            </Badge>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Avertissement important */}
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.4 }}
                      className="mb-4"
                    >
                      <Alert variant="warning" className="border-0 bg-warning bg-opacity-10">
                        <div className="d-flex align-items-start">
                          <FaInfoCircle className="text-warning me-2 mt-1 flex-shrink-0" />
                          <div className="small">
                            <strong>Important :</strong> La modification du pourcentage ne s'applique qu'aux nouvelles utilisations du code promo à partir de maintenant.
                          </div>
                        </div>
                      </Alert>
                    </motion.div>

                    {/* Formulaire de modification */}
                    <Form onSubmit={handleSubmit}>
                      <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.6 }}
                      >
                        <Form.Group className="mb-4">
                          <Form.Label className="fw-semibold">
                            Nouveau pourcentage de remise <span className="text-danger">*</span>
                          </Form.Label>
                          
                          {/* Layout responsive pour le champ et le badge */}
                          <div className="d-flex flex-column flex-sm-row align-items-sm-center gap-3">
                            <div className="flex-grow-1">
                              <Form.Control
                                type="number"
                                min="0"
                                max="100"
                                step="0.1"
                                value={discountPercentage}
                                onChange={(e) => setDiscountPercentage(Number(e.target.value))}
                                disabled={loading}
                                required
                                className="form-control-lg"
                                style={{ borderRadius: '12px' }}
                              />
                            </div>
                            <div className="text-center text-sm-end flex-shrink-0">
                              <div className="small text-muted mb-1">Actuel</div>
                              <Badge 
                                bg={getDiscountBadgeVariant(promoCode.discountPercentage)} 
                                className="px-3 py-2 fs-6"
                              >
                                {promoCode.discountPercentage}%
                              </Badge>
                            </div>
                          </div>
                          
                          <Form.Text className="text-muted mt-2 d-block">
                            Valeur actuelle : {promoCode.discountPercentage}%
                          </Form.Text>
                        </Form.Group>
                      </motion.div>

                      {/* Boutons d'action */}
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.8 }}
                        className="d-flex flex-column flex-sm-row justify-content-end gap-2"
                      >
                        <Button
                          variant="outline-secondary"
                          onClick={handleClose}
                          disabled={loading}
                          className="px-4 py-2 order-2 order-sm-1"
                          style={{ borderRadius: '8px' }}
                        >
                          Annuler
                        </Button>
                        <Button
                          type="submit"
                          variant="primary"
                          disabled={loading || discountPercentage === promoCode.discountPercentage}
                          className="px-4 py-2 d-flex align-items-center justify-content-center order-1 order-sm-2"
                          style={{ borderRadius: '8px' }}
                        >
                          {loading ? (
                            <>
                              <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                                className="me-2"
                              />
                              Modification...
                            </>
                          ) : (
                            <>
                              <FaEdit className="me-2" />
                              Modifier
                            </>
                          )}
                        </Button>
                      </motion.div>
                    </Form>
                  </motion.div>
                )}
              </Modal.Body>
            </motion.div>
          </motion.div>
        </Modal>
      )}
    </AnimatePresence>
  )
}

export default EditPromoCodeModal